import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, InfoSignIcon, Pane } from "evergreen-ui";
import {
    AbstractTableViewProvider, useTableViewContext
} from "../../common/tables/view/AbstractTableViewProvider";
import { TableView } from "../../common/tables/view/TableView";
import { PLPRTableRow } from "../../../../common/tables/product-listing/payout-rule-mapping/PLPRTableRow";
import { ProductListingToPayoutRule, PayoutRule, CostPayoutModifier } from "../../../../common/models";
import { PLPRCreateInput, PLPRUpdateInput } from "../../../../common/inputs/ProductListingToPayoutRuleInput";
import { PayoutRuleCreateInput } from "../../../../common/inputs/PayoutRuleInput";
import { PayoutRuleType } from "../../../../common/enums";
import { QueryInput } from "../../../../common/query-filters";
import {
    queryPayoutRuleMappingsByProductListing,
    useCreatePayoutRuleMappingMutation,
    useUpdatePayoutRuleMappingMutation,
} from "../../datastore/ProductListingToPayoutRuleDataStore";
import { queryPayoutRulesByStore, useCreatePayoutRuleMutation } from "../../datastore/PayoutRuleDataStore";
import { queryAllCostPayoutModifiers } from "../../datastore/CostPayoutModifierDataStore";
import { DefaultPLPRTableConfig } from "./payout-rule-mapping/tables/config/DefaultPLPRTableConfig";
import { PLPRTableCellFactory } from "./payout-rule-mapping/factory/PLPRTableCellFactory";
import { PLPRTableRowSideSheetForm } from "./payout-rule-mapping/tables/PLPRTableRowSideSheetForm";
import { AddAndAssignPayoutRuleForm, AddAndAssignInput } from "./payout-rule-mapping/tables/AddAndAssignPayoutRuleForm";
import { EmptyState } from "../../common/EmptyState";
import { RampartAuthorized } from "../../rampart/RampartAuthorized";

export const PayoutRuleMappingTab: FC<{ productListingId: string; storeId?: string; onDataLoaded?: (hasData: boolean) => void }> = ({ productListingId, storeId, onDataLoaded }) => {
    // Payout rules belong to a store, so a product listing without a store cannot have mappings
    if (!storeId) {
        return (
            <Pane padding={24}>
                <EmptyState
                    title="No store linked"
                    subtitle="This product listing is not linked to a store"
                >
                    <InfoSignIcon size={60} />
                </EmptyState>
            </Pane>
        );
    }

    return (
        <RampartAuthorized requiredGroups={["juniper.self-serve.admin"]}>
            <PayoutRuleMappingTableContent productListingId={productListingId} storeId={storeId} onDataLoaded={onDataLoaded} />
        </RampartAuthorized>
    );
};

const PayoutRuleMappingTableContent: FC<{ productListingId: string; storeId: string; onDataLoaded?: (hasData: boolean) => void }> = ({ productListingId, storeId, onDataLoaded }) => {
    const defaultCreateInput: Partial<PLPRCreateInput> = useMemo(() => {
        return {
            productListingId,
        };
    }, [productListingId]);

    const [showCreateRowForm, setShowCreateRowForm] = useState<boolean>(false);
    const [createInput, setCreateInput] = useState<Partial<PLPRCreateInput>>(defaultCreateInput);
    const [payoutRules, setPayoutRules] = useState<PayoutRule[]>([]);
    const [costPayoutModifiers, setCostPayoutModifiers] = useState<CostPayoutModifier[]>([]);
    const [tableHasData, setTableHasData] = useState<boolean>(true);

    // "Add & Assign Payout Rule" combined form state
    const defaultAddAndAssignInput: AddAndAssignInput = useMemo(() => {
        return { type: PayoutRuleType.vendor };
    }, []);
    const [showAddPayoutRuleForm, setShowAddPayoutRuleForm] = useState<boolean>(false);
    const [addAndAssignInput, setAddAndAssignInput] = useState<AddAndAssignInput>(defaultAddAndAssignInput);

    const loadPayoutRulesForStore = useCallback(() => {
        queryPayoutRulesByStore(storeId)
            .then(setPayoutRules)
            .catch(() => setPayoutRules([]));
    }, [storeId]);

    const loadCostPayoutModifiers = useCallback(() => {
        queryAllCostPayoutModifiers()
            .then(setCostPayoutModifiers)
            .catch(() => setCostPayoutModifiers([]));
    }, []);

    // Load payout rules and CPMs eagerly for inline dropdowns
    useEffect(() => {
        loadPayoutRulesForStore();
        loadCostPayoutModifiers();
    }, [loadPayoutRulesForStore, loadCostPayoutModifiers]);

    const plprTableCellFactory: PLPRTableCellFactory = useMemo(() => {
        const factory = new PLPRTableCellFactory();
        factory.setCostPayoutModifiers(costPayoutModifiers);
        return factory;
    }, [costPayoutModifiers]);

    const queryTableData = useCallback((input: QueryInput<PLPRTableRow>) => {
        return queryPayoutRuleMappingsByProductListing({
            productListingId,
            options: {
                take: input.pageSize,
            },
        });
    }, [productListingId]);

    const [
        createPayoutRuleMapping,
        {
            loading: createLoading,
            error: createError,
            called: createCalled,
        }
    ] = useCreatePayoutRuleMappingMutation();

    const [
        updatePayoutRuleMapping,
        {
            loading: updateLoading,
            called: updateCalled,
            error: updateError,
        }
    ] = useUpdatePayoutRuleMappingMutation();

    const [
        createPayoutRule,
        {
            loading: addRuleLoading,
            error: addRuleError,
            called: addRuleCalled,
            data: createdPayoutRule,
        }
    ] = useCreatePayoutRuleMutation();

    const onUpdateRow = useCallback((
        plprList: ProductListingToPayoutRule[],
        id: string | number,
        columnName: keyof PLPRTableRow,
        value: string | number | boolean | null,
        _label?: string,
    ): ProductListingToPayoutRule[] => {
        return plprList.map((plpr: ProductListingToPayoutRule) => {
            if (plpr.id !== id) {
                return plpr;
            }

            if (columnName === "costPayoutModifierId") {
                const newCpmId = (value as string) || null;
                const newCpm = newCpmId ? costPayoutModifiers.find(m => m.id === newCpmId) ?? null : null;
                updatePayoutRuleMapping({ id: plpr.id, costPayoutModifierId: newCpmId } as PLPRUpdateInput);
                return {
                    ...plpr,
                    costPayoutModifierId: newCpmId,
                    costPayoutModifier: newCpm,
                };
            }

            // Clearing a required numeric cell surfaces as NaN (from
            // `parseFloat("")`) or null. `startThreshold` is a non-nullable Int,
            // so sending null would throw a Prisma error. Skip the mutation and
            // leave the row unchanged.
            // TODO: consolidate this guard — it's duplicated in CostMetadataTab.
            // A `required: boolean` flag on the column config (honored by the
            // default numeric validator in `defaultCellTypeValidators.ts`) would
            // remove the need for every page to repeat this check.
            if (columnName === "startThreshold" &&
                (value === null || (typeof value === "number" && isNaN(value)))) {
                return plpr;
            }

            updatePayoutRuleMapping({ id: plpr.id, [columnName]: value } as PLPRUpdateInput);
            return { ...plpr, [columnName]: value };
        });
    }, [updatePayoutRuleMapping, costPayoutModifiers]);

    const onLocalDataLoaded = useCallback((hasData: boolean) => {
        setTableHasData(hasData);
        onDataLoaded?.(hasData);
    }, [onDataLoaded]);

    const onOpenSideSheet = useCallback(() => {
        setShowCreateRowForm(true);
    }, []);

    const onSubmitSideSheet = useCallback(() => {
        if (!createInput.payoutRuleId || !createInput.startDate) {
            return;
        }
        createPayoutRuleMapping({
            ...createInput,
            startThreshold: createInput.startThreshold ?? 0,
        } as PLPRCreateInput);
    }, [createPayoutRuleMapping, createInput]);

    const onCancelSideSheet = useCallback(() => {
        setShowCreateRowForm(false);
    }, []);

    // "Add & Assign Payout Rule" callbacks
    const onOpenAddPayoutRule = useCallback(() => {
        setShowAddPayoutRuleForm(true);
    }, []);

    const onSubmitAddPayoutRule = useCallback(() => {
        const ruleInput: PayoutRuleCreateInput = {
            storeId,
            name: addAndAssignInput.name!,
            type: addAndAssignInput.type!,
            retailPricePercentApplicable: addAndAssignInput.retailPricePercentApplicable!,
            unitCostPercentApplicable: addAndAssignInput.unitCostPercentApplicable!,
            juniperServiceFeePercentApplicable: addAndAssignInput.juniperServiceFeePercentApplicable!,
            fulfillmentFeePercentApplicable: addAndAssignInput.fulfillmentFeePercentApplicable!,
            processingFeePercentApplicable: addAndAssignInput.processingFeePercentApplicable!,
            shippingSubsidyPercentApplicable: addAndAssignInput.shippingSubsidyPercentApplicable!,
            vendorProfitSplitPercent: addAndAssignInput.vendorProfitSplitPercent!,
        };
        createPayoutRule(ruleInput);
    }, [createPayoutRule, addAndAssignInput, storeId]);

    const onCancelAddPayoutRule = useCallback(() => {
        setShowAddPayoutRuleForm(false);
    }, []);

    // Reset PLPR assign form after successful creation
    useEffect(() => {
        if (!createCalled) {
            return;
        }

        if (!createLoading && !createError) {
            setCreateInput(defaultCreateInput);
            setShowCreateRowForm(false);
        }
    }, [createLoading, createCalled, createError, defaultCreateInput]);

    // After PayoutRule creation succeeds, chain the PLPR assignment
    const lastChainedRuleId = useRef<string | null>(null);
    useEffect(() => {
        if (!addRuleCalled || addRuleLoading || addRuleError || !createdPayoutRule) {
            return;
        }

        const newRuleId = (createdPayoutRule as PayoutRule).id;
        if (lastChainedRuleId.current === newRuleId) {
            return;
        }
        lastChainedRuleId.current = newRuleId;

        const plprInput: PLPRCreateInput = {
            productListingId,
            payoutRuleId: newRuleId,
            startDate: new Date(addAndAssignInput.startDate!),
            startThreshold: addAndAssignInput.startThreshold ?? 0,
            costPayoutModifierId: addAndAssignInput.costPayoutModifierId || undefined,
        };
        createPayoutRuleMapping(plprInput);

        setAddAndAssignInput(defaultAddAndAssignInput);
        setShowAddPayoutRuleForm(false);
        loadPayoutRulesForStore();
    }, [addRuleLoading, addRuleCalled, addRuleError, createdPayoutRule, addAndAssignInput, defaultAddAndAssignInput, productListingId, createPayoutRuleMapping, loadPayoutRulesForStore]);

    const emptyViewProps = useMemo(() => {
        return {
            primaryText: "No payout rules assigned",
            secondaryText: "Assign a payout rule to get started",
            onClickSecondaryText: onOpenSideSheet,
        };
    }, [onOpenSideSheet]);

    const errorViewProps = useMemo(() => {
        return {
            primaryText: "Failed to load payout rule mappings",
            secondaryText: "Refresh the page to try again",
        };
    }, []);

    return (
        <>
            <AbstractTableViewProvider
                queryTableData={queryTableData}
                onUpdateRow={onUpdateRow}
                updateRowLoading={updateLoading}
                updateRowCalled={updateCalled}
                updateRowError={updateError}
                createRowCalled={createCalled}
                createRowError={createError}
                createRowLoading={createLoading}
                tableCellFactory={plprTableCellFactory}
                savedTableConfigProps={{
                    tableName: "plprMapping",
                    identifiers: ["v3"],
                    defaultConfigValue: DefaultPLPRTableConfig,
                }}
            >
                <DataLoadedNotifier onDataLoaded={onLocalDataLoaded} />
                <PayoutRuleWarningBanner onAssignClick={onOpenSideSheet} />
                <TableView
                    errorViewProps={errorViewProps}
                    emptyViewProps={emptyViewProps}
                />
            </AbstractTableViewProvider>
            <PLPRTableRowSideSheetForm
                isShown={showCreateRowForm}
                plprInput={createInput}
                payoutRules={payoutRules}
                costPayoutModifiers={costPayoutModifiers}
                mutationLoading={createLoading}
                mutationCalled={createCalled}
                mutationError={createError}
                hideFloatingButton={!tableHasData}
                onDismiss={onCancelSideSheet}
                onOpen={onOpenSideSheet}
                onSubmit={onSubmitSideSheet}
                onInputChanged={setCreateInput}
            />
            <AddAndAssignPayoutRuleForm
                isShown={showAddPayoutRuleForm}
                input={addAndAssignInput}
                costPayoutModifiers={costPayoutModifiers}
                mutationLoading={addRuleLoading}
                mutationCalled={addRuleCalled}
                mutationError={addRuleError}
                onDismiss={onCancelAddPayoutRule}
                onOpen={onOpenAddPayoutRule}
                onSubmit={onSubmitAddPayoutRule}
                onInputChanged={setAddAndAssignInput}
            />
        </>
    );
};

const DataLoadedNotifier: FC<{ onDataLoaded?: (hasData: boolean) => void }> = ({ onDataLoaded }) => {
    const { data, dataQuerying } = useTableViewContext();
    const lastReported = useRef<boolean | null>(null);

    useEffect(() => {
        if (dataQuerying || !onDataLoaded) {
            return;
        }
        const hasData = data.length > 0;
        if (lastReported.current !== hasData) {
            lastReported.current = hasData;
            onDataLoaded(hasData);
        }
    }, [data, dataQuerying, onDataLoaded]);

    return null;
};

const PayoutRuleWarningBanner: FC<{ onAssignClick: () => void }> = ({ onAssignClick }) => {
    const { data, dataQuerying } = useTableViewContext();

    if (dataQuerying || data.length > 0) {
        return null;
    }

    return (
        <Alert
            intent="danger"
            title="This product listing has no assigned Payout Rules. Payout calculations will not run until at least one Payout Rule is assigned."
            marginBottom={16}
            marginX={16}
            marginTop={16}
        >
            <Button appearance="primary" marginTop={8} onClick={onAssignClick}>
                Assign Payout Rule
            </Button>
        </Alert>
    );
};
